// ===============================================================================
// CreateBusinessGroup.js - Create Business Group Form for SplitMon
// ===============================================================================
// Location: /var/www/wow/frontend/src/CreateBusinessGroup.js
// Premium feature - Form to create a new business expense group
// ===============================================================================

import React, { useState, useEffect } from "react";
import { businessAPI } from "./api";
import "./CreateBusinessGroup.css";

function CreateBusinessGroup({ onBack, onGroupCreated }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Business Types
  const [types, setTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(true);

  // ===============================================================================
  // Currencies
  // ===============================================================================
  const currencies = [
    { code: "EUR", name: "Euro (€)" },
    { code: "USD", name: "US Dollar ($)" },
    { code: "GBP", name: "British Pound (£)" },
    { code: "CHF", name: "Swiss Franc" },
    { code: "SEK", name: "Swedish Krona" },
    { code: "NOK", name: "Norwegian Krone" },
    { code: "DKK", name: "Danish Krone" },
    { code: "PLN", name: "Polish Złoty" },
    { code: "CZK", name: "Czech Koruna" },
    { code: "CAD", name: "Canadian Dollar" },
    { code: "AUD", name: "Australian Dollar" },
    { code: "JPY", name: "Japanese Yen" },
    { code: "CNY", name: "Chinese Yuan" },
    { code: "INR", name: "Indian Rupee" },
  ];

  // ===============================================================================
  // Load Business Types
  // ===============================================================================
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await businessAPI.getTypes();
        if (res.data?.ok) {
          setTypes(res.data.types || []);
        }
      } catch (err) {
        console.error("Load business types error:", err);
        setError("Failed to load business types");
      }
      setLoadingTypes(false);
    };
    loadTypes();
  }, []);

  // ===============================================================================
  // Submit Handler
  // ===============================================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      setError("Business group name is required");
      return;
    }
    if (!selectedTypeId) {
      setError("Please select a business type");
      return;
    }
    
    setLoading(true);
    setError("");
    
    try {
      const res = await businessAPI.createGroup({
        name: name.trim(),
        description: description.trim(),
        type_id: selectedTypeId,
        currency
      });
      
      if (res.data?.ok) {
        console.log("✅ Business group created:", res.data.group);
        onGroupCreated(res.data.group);
      } else {
        setError("Failed to create business group");
      }
    } catch (err) {
      console.error("Create business group error:", err);
      const detail = err?.response?.data?.detail;
      if (detail?.includes("maximum")) {
        setError(detail);
      } else {
        setError(detail || "Failed to create business group");
      }
    }
    
    setLoading(false);
  };

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="create-biz-group-page">
      {/* Header */}
      <div className="cbg-header">
        <button className="cbg-back-btn" onClick={onBack}>
          ← Back
        </button>
        <div className="cbg-header-content">
          <h1>Create Business Group</h1>
          <p>Set up a new group to track shared business expenses</p>
        </div>
      </div>

      {/* Form */}
      <div className="cbg-form-container">
        <form onSubmit={handleSubmit} className="cbg-form">
          {/* Business Type Selection */}
          <div className="cbg-section">
            <label className="cbg-label">Business Type *</label>
            <p className="cbg-help">Select the type that best matches your business</p>
            
            {loadingTypes ? (
              <div className="cbg-types-loading">Loading business types...</div>
            ) : (
              <div className="cbg-types-grid">
                {types.map(type => (
                  <div
                    key={type.id}
                    className={`cbg-type-card ${selectedTypeId === type.id ? 'selected' : ''}`}
                    onClick={() => setSelectedTypeId(type.id)}
                  >
                    <span className="type-icon">{type.icon}</span>
                    <span className="type-name">{type.type_name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Group Name */}
          <div className="cbg-section">
            <label className="cbg-label" htmlFor="name">Group Name *</label>
            <input
              id="name"
              type="text"
              className="cbg-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Acme Corp Q1 2025, Johnson Construction Project"
              disabled={loading}
              maxLength={100}
              required
            />
          </div>

          {/* Description */}
          <div className="cbg-section">
            <label className="cbg-label" htmlFor="description">Description (optional)</label>
            <textarea
              id="description"
              className="cbg-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this business group"
              disabled={loading}
              rows={3}
            />
          </div>

          {/* Currency */}
          <div className="cbg-section">
            <label className="cbg-label" htmlFor="currency">Currency</label>
            <select
              id="currency"
              className="cbg-select"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              disabled={loading}
            >
              {currencies.map(c => (
                <option key={c.code} value={c.code}>
                  {c.code} - {c.name}
                </option>
              ))}
            </select>
          </div>

          {/* Error Message */}
          {error && (
            <div className="cbg-error">
              {error}
              {error.includes("maximum") && (
                <a 
                  href="https://www.split4biz.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="cbg-error-link"
                >
                  Upgrade to Split for Business →
                </a>
              )}
            </div>
          )}

          {/* Submit Button */}
          <button 
            type="submit" 
            className="cbg-submit-btn" 
            disabled={loading || !selectedTypeId}
          >
            {loading ? "Creating..." : "Create Business Group"}
          </button>
        </form>

        {/* Info Card */}
        <div className="cbg-info-card">
          <h3>💡 Tip</h3>
          <p>
            Each business type comes with pre-defined expense categories tailored 
            for that industry. You can also add custom categories after creating 
            the group.
          </p>
        </div>
      </div>
    </div>
  );
}

export default CreateBusinessGroup;

